import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Search,
  Loader2,
  Scale,
  Users as UsersIcon,
  FileText,
  Building2,
  User as UserIcon,
} from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import NewCaseModal from '../components/cases/NewCaseModal'
import {
  CaseStatusLabel,
  LegalAreaLabel,
  type CaseDetail,
  type CaseListItem,
  type CaseListResponse,
  type CaseStatus,
  type LegalArea,
} from '@toga/shared'

const PAGE_SIZE = 25

const statusAccent: Record<CaseStatus, string> = {
  ABIERTA: 'bg-toga-500/15 text-toga-200 border-toga-500/30',
  EN_TRAMITE: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
  SUSPENDIDA: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  ARCHIVADA: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  ARCHIVADA_PROVISIONAL: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  GANADA: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  PERDIDA: 'bg-rose-500/15 text-rose-200 border-rose-500/30',
}

export default function Cases() {
  const auth = useAuth()
  const canCreate =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')

  const [data, setData] = useState<CaseListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<CaseStatus | ''>('')
  const [areaFilter, setAreaFilter] = useState<LegalArea | ''>('')
  const [page, setPage] = useState(1)
  const [showCreate, setShowCreate] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (statusFilter) params.set('status', statusFilter)
      if (areaFilter) params.set('area', areaFilter)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<CaseListResponse>(`/cases?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [q, statusFilter, areaFilter, page])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    setPage(1)
  }, [q, statusFilter, areaFilter])

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">
            {data ? `${data.total} ` : ''}
            Expediente{data?.total === 1 ? '' : 's'}
          </h2>
          <p className="t-muted text-sm mt-1">
            Expedientes en los que participas. Los administradores ven todos.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={16} /> Nuevo expediente
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por código, título, juzgado, n.º procedimiento o parte contraria…"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CaseStatus | '')}
          className="px-3 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-200"
        >
          <option value="">Todos los estados</option>
          {Object.entries(CaseStatusLabel).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value as LegalArea | '')}
          className="px-3 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-200"
        >
          <option value="">Todas las áreas</option>
          {Object.entries(LegalAreaLabel).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              <th className="px-4 py-3 font-medium">Código</th>
              <th className="px-4 py-3 font-medium">Expediente</th>
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Área</th>
              <th className="px-4 py-3 font-medium text-right">Equipo</th>
              <th className="px-4 py-3 font-medium text-right">Docs</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Cargando…
                </td>
              </tr>
            ) : data && data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center t-muted">
                  {q || statusFilter || areaFilter
                    ? 'Sin resultados para esos filtros.'
                    : 'Aún no participas en ningún expediente. Crea uno nuevo o pídele a un compañero que te asigne.'}
                </td>
              </tr>
            ) : (
              data?.items.map((c) => <CaseRow key={c.id} c={c} />)
            )}
          </tbody>
        </table>

        {data && data.total > data.pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base text-sm">
            <span className="t-muted">
              Página {data.page} de {totalPages} · {data.total} expedientes
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
                className="px-3 py-1 rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={data.page >= totalPages}
                className="px-3 py-1 rounded-lg hover:bg-hover t-muted disabled:opacity-30"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <NewCaseModal
          onClose={() => setShowCreate(false)}
          onCreated={(detail: CaseDetail) => {
            setShowCreate(false)
            // Vuelve a cargar la lista; navegamos a la ficha al verla en próxima fase
            void reload()
            // eslint-disable-next-line no-console
            console.log('Creada expediente', detail.code)
          }}
        />
      )}
    </div>
  )
}

function CaseRow({ c }: { c: CaseListItem }) {
  return (
    <tr className="border-b border-base/50 last:border-b-0 hover:bg-hover/50">
      <td className="px-4 py-3">
        <Link to={`/cases/${c.id}`} className="font-mono text-xs text-toga-300 hover:underline">
          {c.code}
        </Link>
      </td>
      <td className="px-4 py-3 t-primary">
        <Link to={`/cases/${c.id}`} className="font-medium hover:underline flex items-center gap-2">
          <Scale size={14} className="text-toga-300" />
          {c.title}
        </Link>
        {c.courtName && (
          <div className="text-xs t-faint mt-0.5">
            {c.courtName}
            {c.courtFileNumber && ` · ${c.courtFileNumber}`}
          </div>
        )}
      </td>
      <td className="px-4 py-3 t-secondary text-xs">
        <Link to={`/clients`} className="hover:underline inline-flex items-center gap-1">
          <Building2 size={12} className="t-faint" />
          {c.clientDisplayName}
        </Link>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${statusAccent[c.status]}`}>
          {CaseStatusLabel[c.status]}
        </span>
      </td>
      <td className="px-4 py-3 t-secondary text-xs">{LegalAreaLabel[c.area]}</td>
      <td className="px-4 py-3 text-right t-muted text-xs">
        <span className="inline-flex items-center gap-1">
          <UsersIcon size={12} /> {c.assignedCount}
        </span>
      </td>
      <td className="px-4 py-3 text-right t-muted text-xs">
        <span className="inline-flex items-center gap-1">
          <FileText size={12} /> {c.documentsCount}
        </span>
      </td>
    </tr>
  )
}

// Avatar pequeño no usado aquí pero exportable más adelante
export function PersonIcon() {
  return <UserIcon size={14} />
}
